import { effectiveRoleKey, isCustomRoleKey } from '@agos/shared'

import type { RolePerson, RoleView } from '../roles/roles-api'
import { orderRoles } from './role-model'
import { parentOf } from './permission-matrix'

/**
 * *Kto čo smie* - who holds one right, and why, `FRONTEND.md` §9.752 (design §2.9).
 *
 * **Legacy has no answer to this question at all** (design §1.7): rights are edited per role and per person, and
 * *who can void a fiscal receipt* had to be worked out by reading every role and every exception. The answer is
 * grouped by **why**, because the why is what somebody acts on: an exception is changed on a person, a role's tick on
 * the role, and the built-in Administrátor's pass not at all.
 */

/** Why a person holds the right - or, for `denied`, why they do not although their role does. */
export type HolderKind = 'main-admin' | 'administrator-pass' | 'role' | 'exception' | 'other' | 'denied'

export interface HolderGroup {
  kind: HolderKind
  /** The role, for `role`. */
  roleKey?: string
  people: RolePerson[]
}

/** What `GET /settings/permissions/employees` says about one person, as far as this question needs it. */
export interface PersonRights {
  overrides: readonly { permission?: string; granted?: boolean }[]
  /** The server's `allowedSet` (§10A.407), or null from a server that does not send it. */
  allowed: readonly string[] | null
}

export interface HoldersContext {
  people: readonly RolePerson[]
  roles: readonly RoleView[]
  catalogue: readonly string[]
  /** By employee id; a person missing here is read off their role alone. */
  rights: ReadonlyMap<number, PersonRights>
}

function isBuiltInAdministrator(person: RolePerson): boolean {
  return person.systemRole === 'admin' && !isCustomRoleKey(person.roleKey)
}

/**
 * Whether the person's role holds the right on its rows, its parent family included.
 *
 * **A parent counts** because the guard names both (`@RequirePermission({"fiskal", "fiskal_storno"})`, FB-103), so a
 * role holding `fiskal` is a role whose people pass the storno - which is exactly what this tab is asked.
 */
function roleHolds(role: RoleView | undefined, key: string, catalogue: ReadonlySet<string>): boolean {
  if (role === undefined) return false
  const rows = new Set(role.permissions)
  if (rows.has(key)) return true
  const parent = parentOf(key, catalogue)
  return parent !== null && rows.has(parent)
}

/**
 * The people who hold `key`, grouped by why - *hlavní administrátori · vstavaní administrátori · rola X (n) · výnimkou
 * udelené · nemá - výnimkou zakázané*.
 *
 * **Held is the server's word when it sends one** (`allowed`, §10A.407), exactly as *Čo smie a prečo* reads it; the
 * reason is derived only for the heading. Without `allowed` - a server from before §10A.408 - the same rules the server
 * applies are applied here: a main administrator always, a personal deny never, a personal grant always, the built-in
 * Administrátor by its pass, and anybody else by their role's rows.
 *
 * **A personal deny of a right the role holds is listed** (`denied`), because it is the answer to *why can't Jana do
 * this when every other waiter can* - and a person whose role does not hold the right and who has no exception is not
 * listed at all: the question is who holds it.
 *
 * Roles come in the cards' order, each with its own people; empty groups are dropped.
 */
export function holdersOf(key: string, context: HoldersContext): HolderGroup[] {
  const known = new Set(context.catalogue)
  const rolesByKey = new Map(context.roles.map((role) => [role.key, role]))
  const mainAdmins: RolePerson[] = []
  const pass: RolePerson[] = []
  const exception: RolePerson[] = []
  const other: RolePerson[] = []
  const denied: RolePerson[] = []
  const byRole = new Map<string, RolePerson[]>()

  for (const person of context.people) {
    const roleKey = effectiveRoleKey(person)
    const rights = context.rights.get(person.id)
    const override = rights?.overrides.find((candidate) => candidate.permission === key)
    const role = rolesByKey.get(roleKey)

    if (person.systemRole === 'main_admin') {
      mainAdmins.push(person)
      continue
    }

    const viaRole = roleHolds(role, key, known)
    const held =
      rights?.allowed != null
        ? rights.allowed.includes(key)
        : override !== undefined
          ? override.granted === true
          : viaRole || isBuiltInAdministrator(person)

    if (!held) {
      if (override !== undefined && override.granted !== true) denied.push(person)
      continue
    }
    if (override?.granted === true) exception.push(person)
    else if (viaRole) byRole.set(roleKey, [...(byRole.get(roleKey) ?? []), person])
    else if (isBuiltInAdministrator(person)) pass.push(person)
    else other.push(person)
  }

  const groups: HolderGroup[] = [{ kind: 'main-admin', people: mainAdmins }, { kind: 'administrator-pass', people: pass }]
  const rank = orderRoles(context.roles).map((role) => role.key)
  const roleKeys = [...byRole.keys()].sort(
    (left, right) =>
      (rank.indexOf(left) === -1 ? rank.length : rank.indexOf(left)) -
        (rank.indexOf(right) === -1 ? rank.length : rank.indexOf(right)) || left.localeCompare(right),
  )
  for (const roleKey of roleKeys) {
    groups.push({ kind: 'role', roleKey, people: byRole.get(roleKey) ?? [] })
  }
  groups.push({ kind: 'exception', people: exception })
  groups.push({ kind: 'other', people: other })
  groups.push({ kind: 'denied', people: denied })
  return groups
    .filter((group) => group.people.length > 0)
    .map((group) => ({ ...group, people: [...group.people].sort((left, right) => left.name.localeCompare(right.name)) }))
}

/** How many hold it - the denied are the ones who do not. */
export function holderCount(groups: readonly HolderGroup[]): number {
  return groups
    .filter((group) => group.kind !== 'denied')
    .reduce((sum, group) => sum + group.people.length, 0)
}
